import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as actions from "../../../actions/index";
import { ACL } from "../../../components/acl";
import { FINANCEADMIN, SUPERADMIN } from "../../../constants/roles";
import Plan from "./subscription/plan";
import AddOn from "./subscription/add_on";
import OneTimeCharge from "./subscription/one_time_charge";
import EditSubscription from "./subscription/edit";
import {
  AccordionSection,
  InfoGrid,
  ErrorBanner,
  MonthlyTotalBar,
  formatMoney,
} from "./subscription/components";

// ── Helpers ────────────────────────────────────────────────────────────────

function formatBillingCycle(duration, unit) {
  if (!unit) return null;
  if (duration === 1 && unit === "month") return "Monthly";
  if (duration === 3 && unit === "month") return "Quarterly";
  if (duration === 1 && unit === "year") return "Annually";
  return `${duration} ${unit}`;
}

function getPeriodLabel(duration, unit) {
  if (!unit) return "/mo";
  if (unit === "year") return "/yr";
  if (duration === 3 && unit === "month") return "/qtr";
  return "/mo";
}

function formatDate(isoString) {
  if (!isoString) return null;
  return new Date(isoString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function buildPricingModelMap(subscriptionItems) {
  const map = {};
  if (!subscriptionItems) return map;
  for (const item of subscriptionItems) {
    for (const price of (item.plan_prices || [])) {
      map[price.plan_price_id] = price.pricing_model;
    }
  }
  return map;
}

function getPlanPricingModel(planId, subscriptionItems) {
  if (!subscriptionItems || !planId) return null;
  for (const item of subscriptionItems) {
    if (item.plan_type !== "plan") continue;
    const price = (item.plan_prices || []).find((p) => p.plan_price_id === planId);
    if (price) return price.pricing_model;
  }
  return null;
}

function groupAddons(addons, subscriptionItems) {
  if (!addons || addons.length === 0) return [];
  const pricingModelMap = buildPricingModelMap(subscriptionItems);
  return [
    {
      category: "Add-ons",
      items: addons.map((a) => {
        const pricingModel = pricingModelMap[a.item_price_id];
        const isPerUnit = pricingModel != null
          ? pricingModel === "per_unit"
          : a.quantity != null;
        return {
          name: a.addon_name || a.item_price_id || null,
          description: null,
          addonType: isPerUnit ? "seat" : "flat",
          qty: isPerUnit
            ? Math.max(a.min_quantity || 0, a.quantity || 0) || null
            : null,
          pricePerSeat: isPerUnit ? a.unit_price : null,
          price: !isPerUnit ? a.unit_price : null,
          total: a.amount || null,
          originalTotal: null,
        };
      }),
    },
  ];
}

function mapSubscriptionData(sub, subscriptionItems) {
  if (!sub) return null;

  const totalCost = sub.cost != null ? sub.cost : null;
  const addonsTotal = (sub.addons || []).reduce(
    (sum, a) => sum + (a.amount || 0),
    0
  );
  const planTotal = totalCost != null ? totalCost - addonsTotal : null;

  return {
    syncError: false,
    syncErrorTitle: null,
    syncErrorDescription: null,
    syncErrorTimestamp: null,
    periodLabel: getPeriodLabel(sub.billing_duration, sub.billing_duration_unit),
    billingCycle: formatBillingCycle(sub.billing_duration, sub.billing_duration_unit),
    nextBillingDate: formatDate(sub.next_billing_at),
    status: sub.status ? sub?.status : null,
    plan: (() => {
      const pricingModel = getPlanPricingModel(sub.plan_id, subscriptionItems);
      const isPerUnit = pricingModel === "per_unit";
      const seats = Math.max(sub.min_no_of_users || 0, sub.no_of_users || 0) || null;
      const pricePerSeat = isPerUnit && planTotal != null && seats
        ? Math.round((planTotal / seats) * 100) / 100
        : null;
      return {
        name: sub.plan_name || sub.plan_id || null,
        pricingModel,
        seats,
        activeSeats: sub.no_of_users || null,
        pricePerSeat,
        flatPrice: !isPerUnit ? planTotal : null,
        total: planTotal,
        originalTotal: null,
      };
    })(),
    addons: groupAddons(sub.addons, subscriptionItems),
    oneTimeCharges: [],
    monthlyTotal: {
      total: totalCost,
      plan: planTotal,
      addons: addonsTotal || null,
    },
    sync: {
      lastSynced: sub.created_at ? `Created ${formatDate(sub.created_at)}` : null,
      source: "Chargebee",
      subscriptionId: sub.reference_subscription_id || null,
      chargebeeCustomerId: sub.reference_customer_id || null,
    },
  };
}

// ── Icons ──────────────────────────────────────────────────────────────────

const PlanIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <path d="M16 3.128a4 4 0 0 1 0 7.744" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
    <circle cx="9" cy="7" r="4" />
  </svg>
);

const AddonIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <path d="M15.39 4.39a1 1 0 0 0 1.68-.474 2.5 2.5 0 1 1 3.014 3.015 1 1 0 0 0-.474 1.68l1.683 1.682a2.414 2.414 0 0 1 0 3.414L19.61 15.39a1 1 0 0 1-1.68-.474 2.5 2.5 0 1 0-3.014 3.015 1 1 0 0 1 .474 1.68l-1.683 1.682a2.414 2.414 0 0 1-3.414 0L8.61 19.61a1 1 0 0 0-1.68.474 2.5 2.5 0 1 1-3.014-3.015 1 1 0 0 0 .474-1.68l-1.683-1.682a2.414 2.414 0 0 1 0-3.414L4.39 8.61a1 1 0 0 1 1.68.474 2.5 2.5 0 1 0 3.014-3.015 1 1 0 0 1-.474-1.68l1.683-1.682a2.414 2.414 0 0 1 3.414 0z" />
  </svg>
);

const OneTimeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    aria-hidden="true">
    <line x1="12" x2="12" y1="2" y2="22" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

// ── Loading Skeleton ───────────────────────────────────────────────────────

function SubscriptionSkeleton() {
  return (
    <div className="animate-pulse p-6 space-y-5">
      <div className="h-4 bg-gray-200 rounded w-1/4" />
      <div className="bg-gray-100 rounded-xl p-5 space-y-3">
        <div className="h-3 bg-gray-200 rounded w-1/3" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-8 bg-gray-200 rounded" />
          <div className="h-8 bg-gray-200 rounded" />
          <div className="h-8 bg-gray-200 rounded" />
        </div>
      </div>
      <div className="h-14 bg-gray-200 rounded-xl" />
      <div className="h-14 bg-gray-200 rounded-xl" />
      <div className="h-14 bg-gray-200 rounded-xl" />
      <div className="h-20 bg-gray-200 rounded-xl" />
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

function Subscriptions({ company_uid }) {
  const [openEdit, setOpenEdit] = useState(false);

  const dispatch = useDispatch();
  const subscriptions = useSelector((state) => state.company.subscriptions);
  const rawSubscriptionItems = useSelector((state) => state.company.subscription_items);
  const subscriptionItems = Array.isArray(rawSubscriptionItems) ? rawSubscriptionItems : null;

  useEffect(() => {
    if (company_uid) {
      dispatch(actions.GETSUBSCRIPTIONS_REQ({ company_uid }));
      dispatch(actions.GETSUBSCRIPTION_ITEMS_REQ());
    }
  }, [company_uid]);

  if (!subscriptions || !subscriptionItems) {
    return (
      <div className="mt-3">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <SubscriptionSkeleton />
        </div>
      </div>
    );
  }

  const data = mapSubscriptionData(subscriptions, subscriptionItems);
  if (!data) return null;

  const { plan, addons, oneTimeCharges, monthlyTotal, sync, periodLabel } = data;

  return (
    <div className="mt-3">
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">

        {/* Card Header */}
        <div className="flex items-center justify-between px-6 pb-3 pt-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Subscription Details
          </h3>
          <div className="flex items-center gap-3">
            {data.syncError ? (
              <div className="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="text-red-500" aria-hidden="true">
                  <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                </svg>
                <span className="text-sm text-red-600">Sync error</span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className="text-[#27815C]" aria-hidden="true">
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                <span className="text-sm text-gray-600">{sync.lastSynced || "—"}</span>
              </div>
            )}
            <ACL showFor={[FINANCEADMIN, SUPERADMIN]}>
              <button
                type="button"
                onClick={() => setOpenEdit(true)}
                className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 bg-gray-800 text-white hover:bg-gray-600 active:bg-gray-700 px-3 py-1.5 text-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  aria-hidden="true">
                  <path d="M13 21h8" />
                  <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                </svg>
                Edit Subscription
              </button>
            </ACL>
          </div>
        </div>
        <div className="border-b mx-6" />

        {/* Error Banner */}
        {data.syncError && (
          <div className="px-6 pt-4">
            <ErrorBanner
              title={data.syncErrorTitle}
              description={data.syncErrorDescription}
              timestamp={data.syncErrorTimestamp}
            />
          </div>
        )}

        {/* Card Body */}
        <div className="p-6 space-y-6">

          {/* Plan Information */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Plan Information
            </h3>
            <InfoGrid
              columns={3}
              items={[
                { label: "Billing Cycle", value: data.billingCycle || "—" },
                { label: "Next Billing Date", value: data.nextBillingDate || "—" },
                { label: "Subscription Status", value: data.status || "—", type: "badge" },
              ]}
            />
          </div>

          {/* Plan Accordion */}
          <AccordionSection
            icon={<PlanIcon />}
            iconBgColor="blue"
            label="Plan"
            title={plan.name || "—"}
            subtitle={`${plan.seats ?? "—"} seats`}
            price={plan.total != null ? `$${formatMoney(plan.total)}${periodLabel}` : "—"}
          >
            <Plan plan={plan} periodLabel={periodLabel} />
          </AccordionSection>

          {/* Add-ons Accordion */}
          <AccordionSection
            icon={<AddonIcon />}
            iconBgColor="purple"
            title="Add-ons"
            subtitle={`${addons.reduce((sum, g) => sum + g.items.length, 0)} active`}
            price={monthlyTotal.addons != null ? `$${formatMoney(monthlyTotal.addons)}${periodLabel}` : "—"}
          >
            <AddOn addons={addons} periodLabel={periodLabel} />
          </AccordionSection>

          {/* One-time Charges Accordion — hidden until API available */}
          {/* <AccordionSection
            icon={<OneTimeIcon />}
            iconBgColor="amber"
            title="One-time Charges"
            subtitle={`${oneTimeCharges.length} charges`}
            price={`$${oneTimeCharges.reduce((sum, c) => sum + (c.originalAmount || c.amount || 0), 0).toLocaleString()}`}
          >
            <OneTimeCharge charges={oneTimeCharges} />
          </AccordionSection> */}

          {/* Monthly Total */}
          <MonthlyTotalBar
            variant="view"
            total={monthlyTotal.total}
            planAmount={monthlyTotal.plan}
            addonsAmount={monthlyTotal.addons}
            periodLabel={periodLabel}
          />

          {/* Sync Status */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Sync Status
            </h3>
            <InfoGrid
              columns={2}
              items={[
                { label: "Last Synced", value: sync.lastSynced || "—", type: "icon-text" },
                { label: "Sync Source", value: sync.source || "—" },
                { label: "Subscription ID", value: sync.subscriptionId || "—", type: "code" },
                { label: "Chargebee Customer ID", value: sync.chargebeeCustomerId || "—", type: "code" },
              ]}
            />
          </div>

        </div>
      </div>

      {openEdit && (
        <EditSubscription company_uid={company_uid} onClose={() => setOpenEdit(false)} />
      )}
    </div>
  );
}

export default Subscriptions;
